'use client';

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import toast from 'react-hot-toast';
import { orderApi } from '../../services/orderApi';

const getStatusColor = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'bg-green-100 text-green-800';
    case 'processing': return 'bg-blue-100 text-blue-800';
    case 'pending': return 'bg-yellow-100 text-yellow-800';
    case 'cancelled': return 'bg-red-100 text-red-800';
    case 'refunded': return 'bg-gray-100 text-gray-800';
    default: return 'bg-gray-100 text-gray-800';
  }
};

const getStatusLabel = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return 'Hoàn thành';
    case 'processing': return 'Đang xử lý';
    case 'pending': return 'Chờ xử lý';
    case 'cancelled': return 'Đã hủy';
    case 'refunded': return 'Hoàn tiền';
    default: return status || 'Không xác định';
  }
};

const getStatusIcon = (status) => {
  switch (status?.toLowerCase()) {
    case 'completed': return '✅';
    case 'processing': return '🚚';
    case 'pending': return '⏰';
    case 'cancelled': return '❌';
    case 'refunded': return '⚠️';
    default: return '📦';
  }
};

const OrderManagement = () => {
  const { user, token } = useAuth();
  const [allOrders, setAllOrders] = useState([]); // Tất cả đơn hàng từ API
  const [filteredOrders, setFilteredOrders] = useState([]); // Đơn hàng đã lọc theo ngày
  const [orders, setOrders] = useState([]); // Đơn hàng hiển thị trên trang hiện tại
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [showOrderDetail, setShowOrderDetail] = useState(false);
  
  // Date range state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [isCustomFilter, setIsCustomFilter] = useState(false); // true: custom filter, false: today filter
  const [filterType, setFilterType] = useState('today'); // today, week, month, year, custom
  
  // Store filter state
  const [selectedStore, setSelectedStore] = useState('all');
  const [availableStores, setAvailableStores] = useState([]);
  
  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalOrders, setTotalOrders] = useState(0);
  const [ordersPerPage] = useState(10);

  // Load data khi component mount
  useEffect(() => {
    loadData();
  }, [token]);

  // Lọc đơn hàng theo ngày, store và tìm kiếm
  const filterOrdersByDateAndStore = (orders, startDate, endDate, isCustom, filterType, selectedStore, searchTerm) => {
    if (!orders || orders.length === 0) return [];
    
    // Lọc theo store trước
    let filteredByStore = orders;
    if (selectedStore !== 'all') {
      filteredByStore = orders.filter(order => order.store === selectedStore);
    }
    
    // Lọc theo tìm kiếm
    let filteredBySearch = filteredByStore;
    if (searchTerm.trim()) {
      const searchLower = searchTerm.toLowerCase();
      filteredBySearch = filteredByStore.filter(order => 
        order.customer_name?.toLowerCase().includes(searchLower) ||
        order.order_id?.toString().includes(searchTerm) ||
        order.customer_email?.toLowerCase().includes(searchLower)
      );
    }
    
    if (isCustom && startDate && endDate) {
      // Lọc theo ngày tùy chỉnh
      const start = new Date(startDate);
      const end = new Date(endDate);
      end.setHours(23, 59, 59, 999); // Set đến cuối ngày
      
      return filteredBySearch.filter(order => {
        const orderDate = new Date(order.created_at || order.date_created);
        return orderDate >= start && orderDate <= end;
      });
    } else {
      // Lọc theo filterType
      const now = new Date();
      let startOfPeriod, endOfPeriod;
      
      switch (filterType) {
        case 'today':
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
          break;
        case 'week':
          // Tuần hiện tại (từ thứ 2 đến chủ nhật)
          const dayOfWeek = now.getDay();
          const daysFromMonday = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate() - daysFromMonday);
          endOfPeriod = new Date(startOfPeriod.getFullYear(), startOfPeriod.getMonth(), startOfPeriod.getDate() + 6, 23, 59, 59, 999);
          break;
        case 'month':
          // Tháng hiện tại
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), 1);
          endOfPeriod = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999);
          break;
        case 'year':
          // Năm hiện tại
          startOfPeriod = new Date(now.getFullYear(), 0, 1);
          endOfPeriod = new Date(now.getFullYear(), 11, 31, 23, 59, 59, 999);
          break;
        default:
          // Mặc định là hôm nay
          startOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
          endOfPeriod = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
      }
      
      return filteredBySearch.filter(order => {
        const orderDate = new Date(order.created_at || order.date_created);
        return orderDate >= startOfPeriod && orderDate <= endOfPeriod;
      });
    }
  };

  // Tính toán đơn hàng hiển thị khi filteredOrders hoặc currentPage thay đổi
  useEffect(() => {
    const startIndex = (currentPage - 1) * ordersPerPage;
    const endIndex = startIndex + ordersPerPage;
    const currentOrders = filteredOrders.slice(startIndex, endIndex);
    
    setOrders(currentOrders);
    setTotalPages(Math.ceil(filteredOrders.length / ordersPerPage));
    setTotalOrders(filteredOrders.length);
  }, [filteredOrders, currentPage, ordersPerPage]);

  // Lọc đơn hàng khi allOrders hoặc filter thay đổi
  useEffect(() => {
    const filtered = filterOrdersByDateAndStore(allOrders, startDate, endDate, isCustomFilter, filterType, selectedStore, searchTerm);
    setFilteredOrders(filtered);
    setCurrentPage(1); // Reset về trang đầu khi filter
  }, [allOrders, startDate, endDate, isCustomFilter, filterType, selectedStore, searchTerm]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Gọi API để lấy tất cả đơn hàng
      const response = await orderApi.getOrdersWithDateRange(token, '/orders');
      
      // Xử lý response từ backend API thực
      if (response.data && response.data.success) {
        setAllOrders(response.data.data || []);
      } else if (response.data && Array.isArray(response.data)) {
        // Nếu response trực tiếp là array
        setAllOrders(response.data);
      } else {
        console.error('Invalid API response format:', response);
        setError('Dữ liệu API không đúng định dạng');
        setAllOrders([]);
      }
      
      // Cập nhật danh sách store từ đơn hàng
      const stores = [...new Set(response.data?.data?.map(order => order.store) || response.data?.map(order => order.store) || [])];
      setAvailableStores(stores);
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Lỗi khi tải dữ liệu: ${error.message}`);
      setAllOrders([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    setCurrentPage(1); // Reset về trang đầu
    await loadData();
    setRefreshing(false);
    toast.success('Đã làm mới dữ liệu');
  };

  const handleDateFilter = async () => {
    // Kiểm tra nếu có ngày bắt đầu và kết thúc
    if (startDate && endDate) {
      setIsCustomFilter(true);
      setFilterType('custom');
      toast.success('Đã lọc theo ngày tùy chỉnh');
    } else {
      // Nếu không có ngày thì lọc theo hôm nay
      setIsCustomFilter(false);
      setStartDate('');
      setEndDate('');
      setFilterType('today');
      toast.success('Đã lọc theo ngày hôm nay');
    }
  };

  const handleFilterChange = (type) => {
    setFilterType(type);
    setIsCustomFilter(false);
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    
    let message = '';
    switch (type) {
      case 'today':
        message = 'Đã lọc theo ngày hôm nay';
        break;
      case 'week':
        message = 'Đã lọc theo tuần này';
        break;
      case 'month':
        message = 'Đã lọc theo tháng này';
        break;
      case 'year':
        message = 'Đã lọc theo năm này';
        break;
      default:
        message = 'Đã lọc dữ liệu';
    }
    
    toast.success(message);
  };



  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      // Gọi API thực để cập nhật trạng thái
      await orderApi.updateOrderStatus(token, orderId, newStatus);
      
      // Update local state
      setAllOrders(prevOrders => 
        prevOrders.map(order => 
          order.id === orderId 
            ? { ...order, order_status: newStatus }
            : order
        )
      );
      
      toast.success('Cập nhật trạng thái thành công');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error(`Lỗi khi cập nhật trạng thái: ${error.message}`);
    }
  };



  const formatCurrency = (amount) => {
    if (!amount) return '0 ₫';
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(parseFloat(amount) * 24000); // Convert USD to VND (approximate)
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
    setShowOrderDetail(true);
  };

  const parseProducts = (productsJson) => {
    try {
      if (!productsJson) return [];
      return JSON.parse(productsJson);
    } catch (error) {
      console.error('Error parsing products:', error);
      return [];
    }
  };

  const getProductVariation = (product) => {
    if (product.variation_attributes && Object.keys(product.variation_attributes).length > 0) {
      return Object.entries(product.variation_attributes)
        .map(([key, value]) => `${key.replace('attribute_', '').replace('pa_', '')}: ${value}`)
        .join(', ');
    }
    
    if (product.meta_data) {
      const variations = product.meta_data
        .filter(meta => meta.key.includes('attribute_') || meta.key.includes('pa_'))
        .map(meta => `${meta.key.replace('attribute_', '').replace('pa_', '')}: ${meta.value}`)
        .join(', ');
      
      return variations;
    }
    
    return '';
  };



  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600"></div>
      </div>
    );
  }

    return (
    <div className="p-2 sm:p-4 lg:p-6 max-w-full overflow-hidden">
        {/* Fixed Header */}
        <div className="bg-white rounded-lg shadow-lg mb-4 sm:mb-6 sticky top-0 z-10 max-w-full">
          {/* Title and Refresh */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="flex justify-between items-center">
              <div>
                <h1 className="text-lg sm:text-xl font-bold text-gray-900">Quản lý đơn hàng</h1>
              </div>
              <button
                onClick={handleRefresh}
                disabled={refreshing}
                className="px-2 py-1 sm:px-3 sm:py-1.5 bg-blue-600 text-white rounded text-xs sm:text-sm hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1 sm:gap-2"
              >
                {refreshing ? (
                  <>
                    <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    <span className="hidden sm:inline">Đang tải...</span>
                  </>
                ) : (
                  <>
                    🔄 <span className="hidden sm:inline">Làm mới</span>
                  </>
                )}
              </button>
            </div>
          </div>

          {/* Statistics */}
          <div className="p-3 sm:p-4 border-b border-gray-200">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-3">
              <div className="flex items-center">
                <div className="p-1 sm:p-1.5 bg-blue-100 rounded text-xs sm:text-sm">
                  🛒
                </div>
                <div className="ml-1 sm:ml-2">
                  <p className="text-xs font-medium text-gray-600">Tổng đơn hàng</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    {totalOrders}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="p-1 sm:p-1.5 bg-green-100 rounded text-xs sm:text-sm">
                  💰
                </div>
                <div className="ml-1 sm:ml-2">
                  <p className="text-xs font-medium text-gray-600">Doanh thu</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    {formatCurrency(allOrders.reduce((sum, order) => sum + parseFloat(order.total || 0), 0))}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="p-1 sm:p-1.5 bg-yellow-100 rounded text-xs sm:text-sm">
                  ✅
                </div>
                <div className="ml-1 sm:ml-2">
                  <p className="text-xs font-medium text-gray-600">Đã hoàn thành</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    {allOrders.filter(order => order.order_status === 'completed').length}
                  </p>
                </div>
              </div>

              <div className="flex items-center">
                <div className="p-1 sm:p-1.5 bg-orange-100 rounded text-xs sm:text-sm">
                  🚚
                </div>
                <div className="ml-1 sm:ml-2">
                  <p className="text-xs font-medium text-gray-600">Đang xử lý</p>
                  <p className="text-xs sm:text-sm font-bold text-gray-900">
                    {allOrders.filter(order => order.order_status === 'processing').length}
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="p-2 sm:p-4">
            <div className="flex flex-col gap-2">
              {/* Search Input */}
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="🔍 Tìm kiếm..."
                className="px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent w-full"
              />
              
              <div className="flex gap-1 sm:gap-2 w-full">
                {/* Date Filter */}
                <select
                  value={isCustomFilter ? 'custom' : filterType}
                  onChange={(e) => {
                    const value = e.target.value;
                    if (value === 'custom') {
                      setIsCustomFilter(true);
                      setFilterType('custom');
                    } else {
                      setIsCustomFilter(false);
                      setFilterType(value);
                      setStartDate('');
                      setEndDate('');
                    }
                  }}
                  className="px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent flex-1"
                >
                  <option value="today">📅 Hôm nay</option>
                  <option value="week">📊 Tuần này</option>
                  <option value="month">📈 Tháng này</option>
                  <option value="year">📅 Năm này</option>
                  <option value="custom">🔍 Tùy chỉnh</option>
                </select>
                
                {/* Store Filter */}
                <select
                  value={selectedStore}
                  onChange={(e) => setSelectedStore(e.target.value)}
                  className="px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent flex-1"
                >
                  <option value="all">🏪 Tất cả store</option>
                  {availableStores.map((store, index) => (
                    <option key={index} value={store}>
                      🏪 {store}
                    </option>
                  ))}
                </select>
              </div>
              
              {/* Custom Date Range - chỉ hiển thị khi chọn "Tùy chỉnh" */}
              {isCustomFilter && (
                <div className="flex flex-col sm:flex-row gap-2 w-full">
                  <input
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    className="px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                  <span className="text-xs text-gray-500 self-center">đến</span>
                  <input
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    className="px-2 py-1.5 border border-gray-300 rounded text-xs sm:text-sm focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-transparent"
                  />
                  <button
                    onClick={handleDateFilter}
                    className="px-2 py-1.5 bg-green-500 text-white rounded text-xs sm:text-sm hover:bg-green-600 transition-colors"
                  >
                    🔍
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

      {/* Error Display */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">
                Lỗi khi tải dữ liệu
              </h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{error}</p>
              </div>
            </div>
          </div>
        </div>
      )}

              {/* Orders Table */}
        <div className="bg-white rounded-lg shadow overflow-hidden max-w-full">
          {loading ? (
            <div className="p-4 sm:p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 sm:h-12 sm:w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-sm sm:text-base text-gray-600">Đang tải dữ liệu...</p>
            </div>
          ) : (
            <div className="overflow-x-auto max-w-full">
              <table className="min-w-full divide-y divide-gray-200 max-w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Khách hàng
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Store
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tổng tiền
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Chi phí
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Trạng thái
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Phương thức
                </th>
                <th className="px-1 sm:px-2 lg:px-6 py-1 sm:py-2 lg:py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ngày tạo
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {orders.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                    <div className="flex flex-col items-center">
                      <div className="text-4xl mb-4">📦</div>
                      <p className="text-lg font-medium">Không có đơn hàng nào</p>
                      <p className="text-sm">Chưa có đơn hàng nào trong hệ thống</p>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => (
                  <tr 
                    key={order.id} 
                    className="hover:bg-gray-50 cursor-pointer transition-colors"
                    onClick={() => handleOrderClick(order)}
                  >
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap text-xs font-medium text-gray-900">
                      #{order.order_id}
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap">
                      <div>
                        <div className="text-xs font-medium text-gray-900">
                          {order.customer_name}
                        </div>
                        <div className="text-xs text-gray-500">
                          {order.customer_email}
                        </div>
                        <div className="text-xs text-gray-400">
                          {order.phone}
                        </div>
                      </div>
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap">
                      <div className="text-xs text-gray-900">
                        <div className="font-medium">{order.store}</div>
                        <div className="text-xs text-gray-500">{order.seller_mail}</div>
                      </div>
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap">
                      <div className="text-xs font-bold text-blue-600">
                        {formatCurrency(order.total)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Phí ship: {formatCurrency(order.shipping_fee)}
                      </div>
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap">
                      <div className="text-xs font-bold text-red-600">
                        {formatCurrency(order.cost)}
                      </div>
                      <div className="text-xs text-gray-500">
                        Chi phí sản xuất
                      </div>
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center px-1 sm:px-1.5 lg:px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(order.order_status)}`}>
                          <span className="mr-1">{getStatusIcon(order.order_status)}</span>
                          <span className="hidden sm:inline">{getStatusLabel(order.order_status)}</span>
                        </span>
                      </div>
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap text-xs text-gray-900">
                      {order.payment_method}
                    </td>
                    <td className="px-1 sm:px-2 lg:px-6 py-2 sm:py-3 lg:py-4 whitespace-nowrap text-xs text-gray-900">
                      {formatDate(order.date_created)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="bg-white rounded-lg shadow p-3 sm:p-4 mt-4 sm:mt-6 max-w-full">
          <div className="flex flex-col sm:flex-row justify-between items-center">
            <div className="text-xs sm:text-sm text-gray-700 mb-2 sm:mb-0">
              Hiển thị {orders.length} trong tổng số {totalOrders} đơn hàng
              {isCustomFilter ? (
                <span className="text-blue-600 ml-1 sm:ml-2">
                  (Lọc theo ngày: {startDate} - {endDate})
                </span>
              ) : (
                <span className="text-green-600 ml-1 sm:ml-2">
                  {filterType === 'today' && '(Lọc theo ngày hôm nay)'}
                  {filterType === 'week' && '(Lọc theo tuần này)'}
                  {filterType === 'month' && '(Lọc theo tháng này)'}
                  {filterType === 'year' && '(Lọc theo năm này)'}
                </span>
              )}
              {selectedStore !== 'all' && (
                <span className="text-purple-600 ml-1 sm:ml-2">
                  (Store: {selectedStore})
                </span>
              )}
              {searchTerm && (
                <span className="text-orange-600 ml-1 sm:ml-2">
                  (Tìm kiếm: "{searchTerm}")
                </span>
              )}
            </div>
            <nav className="flex items-center space-x-2">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                ← Trước
              </button>
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`px-3 py-2 rounded-lg transition-colors ${
                        currentPage === pageNum
                          ? 'bg-blue-600 text-white'
                          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-3 py-2 bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
              >
                Sau →
              </button>
            </nav>
          </div>
        </div>
      )}

      {/* Order Detail Modal */}
      {showOrderDetail && selectedOrder && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-10 sm:top-20 mx-auto p-3 sm:p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white max-h-[90vh] overflow-y-auto">
            <div className="mt-3">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-medium text-gray-900">
                  Chi tiết đơn hàng #{selectedOrder.order_id}
                </h3>
                <button
                  onClick={() => setShowOrderDetail(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  ×
                </button>
              </div>

              <div className="space-y-6">
                {/* Customer Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Thông tin khách hàng</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Tên:</label>
                      <p className="text-sm font-medium">{selectedOrder.customer_name}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Email:</label>
                      <p className="text-sm font-medium">{selectedOrder.customer_email}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Số điện thoại:</label>
                      <p className="text-sm font-medium">{selectedOrder.phone}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Địa chỉ:</label>
                      <p className="text-sm font-medium">
                        {selectedOrder.address}, {selectedOrder.city}, {selectedOrder.state} {selectedOrder.zipcode}, {selectedOrder.country}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Store Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Thông tin cửa hàng</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Store:</label>
                      <p className="text-sm font-medium">{selectedOrder.store}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Seller Email:</label>
                      <p className="text-sm font-medium">{selectedOrder.seller_mail}</p>
                    </div>
                  </div>
                </div>

                {/* Order Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Thông tin đơn hàng</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">ID đơn hàng:</label>
                      <p className="text-sm font-medium">#{selectedOrder.order_id}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Trạng thái:</label>
                      <div className="mt-1">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedOrder.order_status)}`}>
                          <span className="mr-1">{getStatusIcon(selectedOrder.order_status)}</span>
                          <span>{getStatusLabel(selectedOrder.order_status)}</span>
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Phương thức thanh toán:</label>
                      <p className="text-sm font-medium">{selectedOrder.payment_method}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Ngày tạo:</label>
                      <p className="text-sm font-medium">{formatDate(selectedOrder.date_created)}</p>
                    </div>
                  </div>
                </div>

                {/* Financial Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Thông tin tài chính</h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">Tổng tiền:</label>
                      <p className="text-lg font-bold text-blue-600">{formatCurrency(selectedOrder.total)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Phí vận chuyển:</label>
                      <p className="text-sm font-medium">{formatCurrency(selectedOrder.shipping_fee || 0)}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Chi phí:</label>
                      <p className="text-sm font-medium">{formatCurrency(selectedOrder.cost || 0)}</p>
                    </div>
                  </div>
                </div>

                {/* Products */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Sản phẩm đã đặt</h4>
                  {selectedOrder.products && parseProducts(selectedOrder.products).length > 0 ? (
                    <div className="space-y-3">
                      {parseProducts(selectedOrder.products).map((product, index) => (
                        <div key={index} className="border border-gray-200 rounded-lg p-4 bg-gray-50">
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900">
                                {product.name || `Product ID: ${product.product_id}`}
                              </h5>
                              <div className="mt-1 text-sm text-gray-600">
                                <span>Số lượng: {product.quantity || product.qty || 1}</span>
                                {product.price && (
                                  <>
                                    <span className="mx-2">•</span>
                                    <span>Đơn giá: {formatCurrency(product.price)}</span>
                                  </>
                                )}
                                {product.sku && (
                                  <>
                                    <span className="mx-2">•</span>
                                    <span>SKU: {product.sku}</span>
                                  </>
                                )}
                              </div>
                              {getProductVariation(product) && (
                                <div className="mt-1 text-sm text-gray-600">
                                  <span>Thuộc tính: {getProductVariation(product)}</span>
                                </div>
                              )}
                              {product.image_url && (
                                <div className="mt-2">
                                  <img 
                                    src={product.image_url} 
                                    alt={product.name}
                                    className="w-16 h-16 object-cover rounded border"
                                  />
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              {product.total && (
                                <p className="font-bold text-blue-600">{formatCurrency(product.total)}</p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-gray-500 italic">Không có thông tin sản phẩm chi tiết</p>
                  )}
                </div>

                {/* Additional Information */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-3">Thông tin bổ sung</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-sm text-gray-600">IP Address:</label>
                      <p className="text-sm font-medium">{selectedOrder.ip_address}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">UTM Source:</label>
                      <p className="text-sm font-medium">{selectedOrder.utm_source}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Session Pages:</label>
                      <p className="text-sm font-medium">{selectedOrder.session_pages}</p>
                    </div>
                    <div>
                      <label className="text-sm text-gray-600">Ghi chú:</label>
                      <p className="text-sm font-medium">{selectedOrder.note || 'Không có ghi chú'}</p>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 flex justify-end">
                <button
                  onClick={() => setShowOrderDetail(false)}
                  className="px-4 py-2 bg-gray-300 text-gray-700 rounded-lg hover:bg-gray-400 transition-colors"
                >
                  Đóng
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderManagement; 